import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { useMemo } from "react";

export const isQuoteRequired = (productContext: ProductContextModel | CartLineModel | null | undefined): boolean => {
    if (!productContext) {
        return false;
    }

    if ("product" in productContext) {
        const { product, productInfo } = productContext;
        const isZeroPrice = productInfo?.pricing?.actualPrice === 0;
        if (isZeroPrice) {
            return true;
        }

        // Failed real-time pricing (e.g. "no products found") is treated as quote required on PDP.
        if (productInfo?.failedToLoadPricing) {
            return true;
        }

        // Realtime inventory is the source of truth for quote vs buy once it has loaded.
        // Catalog product.quoteRequired must not override inventory.isQuoteRequired.
        const inventory = productInfo?.inventory;
        if (inventory) {
            const isQuoteRequiredFromInventory = inventory.additionalResults?.isQuoteRequired;
            if (isQuoteRequiredFromInventory?.toLowerCase() === "true") {
                return true;
            }
            if (isQuoteRequiredFromInventory?.toLowerCase() === "false") {
                return false;
            }
            // Inventory loaded but flag missing — do not fall back to catalog quoteRequired.
            return false;
        }

        // Inventory not loaded yet — temporary catalog fallback until realtime inventory arrives.
        return !!product?.quoteRequired;
    }

    return productContext.quoteRequired || productContext.pricing?.actualPrice === 0;
};

const useCustomQuoteRequired = (productContext: ProductContextModel | CartLineModel | null | undefined): boolean => {
    return useMemo(() => isQuoteRequired(productContext), [productContext]);
};

export default useCustomQuoteRequired;
